/**
 * 物件ストア V2 (Pinia) — 新テンプレート (災害時避難先のご案内) 用。
 *
 * 旧 `propertyStore` とは別の localStorage キーで独立管理する
 * (旧デザインのデータと混在させない)。永続化方式は Firebase 設定 (§4.3) の有無で分岐する:
 * - 未設定 (isFirebaseConfigured()===false): 現行どおり localStorage (persisted-crud)。
 * - 設定済み: Firestore (firestore-crud)。auth (§7) の status が 'ready' になるまで
 *   crud を生成しない (email 未確定のまま Firestore へアクセスしないため)。
 */
import { defineStore } from 'pinia';
import { computed, ref, shallowRef, watch, type Ref } from 'vue';
import type {
  DraftPropertyV2,
  PropertyV2,
  DestinationKind,
} from '@/types/property-v2';
import { cleanPlaceLabel } from '@/lib/evacuation/place-label';
import { sanitizeWaypoints } from '@/lib/evacuation/waypoints';
import seedData from '@/data/properties.seed.json';
import { generateId, nowIso } from '@/lib/id';
import { createPersistedCrud, type PersistedCrud } from '@/lib/stores/persisted-crud';
import { createFirestoreCrud, type FirestoreCrud } from '@/lib/stores/firestore-crud';
import { isFirebaseConfigured } from '@/lib/firebase/init';
import { currentEditor, withEditStamp } from './edit-stamp';
import { ensureSeedsV2 } from '@/lib/firebase/bootstrap';
import { useAuthStore } from '@/stores/authStore';
import { migrateLegacyPoiCategories } from '@/lib/map/poi-reference';
import type { SaveState } from '@/lib/firebase/save-pipeline';

const STORAGE_KEY = 'evacuation-map.properties.v2';

/**
 * ストアが実際に生成済みかどうか (AppHeader のログアウト flush 用)。
 * 未使用のままログアウトした場合に、flush のためだけに Firestore 初期化を
 * 走らせない (signOut と競合して未処理 rejection になる)。
 */
let storeInstantiated = false;


/** プロジェクト同梱の既定物件 (初回起動時のみ投入)。編集は localStorage が優先。 */
const SEED_PROPERTIES = seedData as unknown as PropertyV2[];

/** 既定物件のディープコピー (imported module を書き換えないため)。 */
function cloneSeed(): PropertyV2[] {
  return SEED_PROPERTIES.map((p) => JSON.parse(JSON.stringify(p)) as PropertyV2);
}

/**
 * src の値を dst へ即時反映する小ヘルパ (immediate: true)。
 * 別ユーザーでの再ログイン時に張り替える必要があるため、停止関数を返す
 * (呼び出し側は再バインド前に必ずこれを呼んで前回分を解除すること)。
 */
function watchBinding<S, D extends S>(src: Ref<D>, dst: Ref<S>): () => void {
  return watch(
    src,
    (v) => {
      dst.value = v;
    },
    { immediate: true },
  );
}

/**
 * ストア未生成なら何もしない、生成済みなら flush を呼ぶ。
 * ログアウト時に未訪問の物件画面によって Firestore を初期化せず、
 * signOut との競合 (permission-denied) を回避する。
 */
export async function flushPropertyStoreV2IfActive(): Promise<void> {
  if (!storeInstantiated) return;
  const store = usePropertyStoreV2();
  await store.flushAll();
}

export const usePropertyStoreV2 = defineStore('property-v2', () => {
  // ストア生成時にフラグをセット (defineStore は一度だけ実行される)
  storeInstantiated = true;
  // 旧 status='reviewed' (設定 UI が既に廃止済み) を 'draft' へ落とす後方互換移行。
  // ロードされる実データは型更新前の3値 (draft/reviewed/exported) を含み得るため、
  // PropertyV2['status'] は現行2値だが実行時は unknown 扱いで安全にキャストする。
  const normalizeStatus = (p: PropertyV2): PropertyV2 =>
    (p.status as unknown) === 'reviewed' ? { ...p, status: 'draft' } : p;

  const normalize = (raw: PropertyV2): PropertyV2 => {
    let p = normalizeStatus(raw);
    // 参考ラベルの後方互換移行 (旧 'medical'→hospital+pharmacy、
    // 'supermarket'→supermarket+building)。移行不要なら同一参照が返る。
    if (p.referenceLabels?.categories) {
      const migrated = migrateLegacyPoiCategories(p.referenceLabels.categories);
      if (migrated !== p.referenceLabels.categories) {
        p = {
          ...p,
          referenceLabels: { ...p.referenceLabels, categories: migrated },
        };
      }
    }
    // 避難先ラベル (name.ja) を表示用に整形 (公式名の行政接頭辞・棟・階/ハザード注記を落とす)。
    // name.ja は選択専用の表示スナップショット (手動編集不可) なので無条件・冪等に適用してよい。
    for (const kind of ['evacuationArea', 'shelter'] as DestinationKind[]) {
      const d = p.destinations[kind];
      const cleaned = cleanPlaceLabel(d.name.ja);
      const wp = sanitizeWaypoints(d.waypoints);
      if (cleaned !== d.name.ja || wp !== d.waypoints) {
        p = {
          ...p,
          destinations: {
            ...p.destinations,
            [kind]: { ...d, name: { ...d.name, ja: cleaned }, waypoints: wp },
          },
        };
      }
    }
    return p;
  };

  const firestoreMode = isFirebaseConfigured();
  const crud = shallowRef<PersistedCrud<PropertyV2> | FirestoreCrud<PropertyV2> | undefined>(
    undefined,
  );
  const properties = computed(() => crud.value?.items.value ?? []);
  const ready = ref(false);
  const saveState = ref<SaveState | 'local'>('local');

  function getProperty(id: string): PropertyV2 | undefined {
    return crud.value?.get(id);
  }

  function createProperty(draft: DraftPropertyV2): PropertyV2 {
    const property: PropertyV2 = {
      ...draft,
      id: generateId('propv2'),
      status: 'draft',
      createdAt: nowIso(),
      updatedAt: nowIso(),
      updatedBy: currentEditor(),
    };
    crud.value?.insert(property);
    return property;
  }

  function updateProperty(
    id: string,
    patch: Partial<Omit<PropertyV2, 'id' | 'createdAt'>>,
  ): void {
    crud.value?.update(id, withEditStamp(patch));
  }

  function deleteProperty(id: string): void {
    crud.value?.remove(id);
  }

  /**
   * updatedAt のみ現在時刻へ更新する。
   *
   * PropertyEditorV2 は property オブジェクトを直接 mutation して編集する
   * (persisted-crud / firestore-crud いずれも items の deep watch で変更を検知する)
   * ため、updateProperty を通らず updatedAt が進まない。編集操作の集約点から
   * これを呼んで一覧の「更新日時」ソートを陳腐化させない。
   */
  function touch(id: string): void {
    const p = crud.value?.get(id);
    if (!p) return;
    p.updatedAt = nowIso();
    p.updatedBy = currentEditor();
  }

  /**
   * 一覧で重複作成しないよう、既存物件があるかを確認してから種データを投入する。
   * Firestore モードではシード再投入は管理者操作のみ (§6-5) のため no-op。
   */
  function upsertProperty(property: PropertyV2): void {
    if (firestoreMode) {
      console.warn(
        '[propertyStoreV2] upsertProperty は Firestore モードでは no-op です (シード再投入は管理者操作のみ)',
      );
      return;
    }
    const idx = properties.value.findIndex((p) => p.id === property.id);
    if (idx >= 0) properties.value[idx] = property;
    else properties.value.push(property);
  }

  /**
   * 既定物件を再投入する (idごとに上書き; ユーザーが追加した他物件は保持)。
   * Firestore モードではシード再投入は管理者操作のみ (§6-5) のため no-op —
   * upsertProperty 側の warn を1件ずつ (SEED_PROPERTIES 件数分) 連発させないよう、
   * ここで早期 return して単発の警告に留める。
   */
  function resetToSeed(): void {
    if (firestoreMode) {
      console.warn(
        '[propertyStoreV2] resetToSeed は Firestore モードでは no-op です (シード再投入は管理者操作のみ)',
      );
      return;
    }
    for (const p of cloneSeed()) upsertProperty(p);
  }

  function flushAll(): Promise<void> {
    const c = crud.value;
    return c && 'flushAll' in c ? c.flushAll() : Promise.resolve();
  }

  if (firestoreMode) {
    // ---- Firestore モード (§4.3) ----
    const auth = useAuthStore();

    /** 現在の crud がどの email にバインドされているか (別ユーザー再ログイン検知用)。 */
    let boundEmail: string | null = null;
    /** ready/saveState の watchBinding 停止関数 (再バインド前に解除する)。 */
    let stopBindings: Array<() => void> = [];
    /** 現在の fsCrud の dispose (次のユーザーへ張り替える際に呼ぶ)。 */
    let disposeCurrent: (() => void) | null = null;

    const initFirestore = (email: string, displayName: string | null): void => {
      for (const stop of stopBindings) stop();
      stopBindings = [];
      const fsCrud = createFirestoreCrud<PropertyV2>({
        kind: 'properties_v2',
        email,
        displayName,
        normalize,
        deferLoad: true,
      });
      crud.value = fsCrud;
      stopBindings.push(watchBinding(fsCrud.ready, ready));
      stopBindings.push(watchBinding(fsCrud.saveState, saveState));
      disposeCurrent = fsCrud.dispose;
      boundEmail = email;
      void (async () => {
        try {
          await ensureSeedsV2(email, displayName); // §6-5 フラグdoc + transaction
        } catch (e) {
          // シード注入の失敗 (transaction競合/瞬断) で閲覧まで道連れにしない。
          // 注入は他クライアントが成功し得るし、既存docの表示は常に可能であるべき。
          console.warn('[propertyStoreV2] シード注入に失敗 (閲覧は継続)', e);
        }
        fsCrud.startLoad();
      })();
    };

    // auth.status の遷移を継続監視する (初回 ready で止めない)。
    // ログアウト→別ユーザー再ログイン (ready(A) → signed-out → ready(B)) を検知し、
    // 旧 crud を dispose してから張り替える。email 未確定のうちは crud を作らない
    // (auth.email ?? 'unknown' 相当のフォールバックは使わない)。
    watch(
      () => auth.status,
      (status) => {
        if (status === 'ready' && auth.email && auth.email !== boundEmail) {
          // 旧 crud があれば dispose する (ログアウト時点で flush 済み想定のため
          // flushAll() は待たない)。
          disposeCurrent?.();
          initFirestore(auth.email, auth.displayName);
        } else if (status !== 'ready') {
          // signed-out 等: ルートガードが画面を Login に飛ばす。crud 自体は残し、
          // dispose は次回 ready 時の張り替え (initFirestore 冒頭) で行う。
          // 同一ユーザーの再ログインでも再バインドさせる
          boundEmail = null;
          ready.value = false;
        }
      },
      { immediate: true },
    );
  } else {
    // ---- localStorage モード (現行どおり・変更禁止) ----
    // 初回 (localStorage 未作成) は既定物件を投入し、即保存して以降は編集内容を優先。
    const firstRun = localStorage.getItem(STORAGE_KEY) === null;
    crud.value = createPersistedCrud<PropertyV2>({
      storageKey: STORAGE_KEY,
      label: '物件データ(V2)',
      initial: cloneSeed, // 初回のみ既定物件
      normalize,
    });
    if (firstRun) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(properties.value));
      } catch (e) {
        console.warn('既定物件(V2)の初期保存に失敗しました', e);
      }
    }

    // 既定物件は initial (初回のみ) で投入済み。
    // 以前はここで SEED_VERSION を比べ、違えば resetToSeed() で保存済みデータを
    // 同梱シードで上書きしていた。シードの修正を配れる一方、ユーザーの編集を
    // 開発側の都合で書き換える経路でもあったため廃止した (resetToSeed は
    // 手動リセット用に残している)。
    ready.value = true;
  }

  return {
    properties,
    getProperty,
    createProperty,
    updateProperty,
    deleteProperty,
    touch,
    upsertProperty,
    resetToSeed,
    ready,
    saveState,
    flushAll,
  };
});
